/**
 * ResearchDetailDrawer — knowledge-base record detail for a Research dashboard result.
 *
 * Renders type-specific detail (monograph fields / clinic-document meta + download /
 * resource link) inside the shared DetailDrawer. Document download reuses the existing
 * globalFileResourcesApi (never a second download path). A "Discuss with Xoe" action
 * (footer) is offered when Xoe is enabled.
 *
 * Literature hits do NOT come here — they open LiteratureArticleDrawer, which fetches the
 * full PubMed record (structured abstract + full-text links) rather than re-rendering the
 * search hit's citation meta.
 *
 * Width is the §16 standard 70vw with a two-column body, matching LiteratureArticleDrawer:
 * prose (summary, mechanism of action) reads in a measure-capped main column, and the looked-up
 * facts sit in a sidebar rather than stretching the prose across the viewport.
 */

import React, { useState } from 'react';
import { Typography, Button, Space, Tag, Grid } from 'antd';
import {
	DownloadOutlined,
	ExportOutlined,
	MessageOutlined,
} from '@ant-design/icons';

import { DetailDrawer } from '../../components/shared';
import { downloadGlobalFileResource } from '../../api/file/globalFileResourcesApi';
import { useNotifications } from '../../context/NotificationContext';
import type { ResearchRecord } from '../../types/research/ResearchTypes';

const { Text, Paragraph, Link } = Typography;

/** Reading measure for prose — mirrors LiteratureArticleDrawer. */
const PROSE_MAX_W = 720;
const META_COL_W = 300;

// ── Field row ───────────────────────────────────────────────────────────────────

/** Mirrors LiteratureArticleDrawer's Field — `.mg-label` (§4) is the system's heading for facts. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div style={{ marginBottom: 14 }}>
		<Text className="mg-label" style={{ display: 'block', marginBottom: 4 }}>
			{label}
		</Text>
		<div style={{ fontSize: 'var(--mg-fs-md)', color: 'var(--mg-text)', lineHeight: 1.6 }}>{children}</div>
	</div>
);

const TagList: React.FC<{ values?: string[] }> = ({ values }) => {
	if (!values || values.length === 0) return <Text type="secondary">—</Text>;
	return (
		<Space size={4} wrap>
			{values.map((v, i) => (
				<Tag key={`${v}-${i}`} style={{ fontSize: 'var(--mg-fs-xs)' }}>
					{v}
				</Tag>
			))}
		</Space>
	);
};

// ── Helpers ─────────────────────────────────────────────────────────────────────

function humanFileSize(bytes: unknown): string | null {
	const n = typeof bytes === 'number' ? bytes : Number(bytes);
	if (!Number.isFinite(n) || n <= 0) return null;
	const units = ['B', 'KB', 'MB', 'GB'];
	let size = n;
	let u = 0;
	while (size >= 1024 && u < units.length - 1) {
		size /= 1024;
		u += 1;
	}
	return `${size.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

function str(v: unknown): string | null {
	return typeof v === 'string' && v.length > 0 ? v : v != null && v !== '' ? String(v) : null;
}

// ── Component ────────────────────────────────────────────────────────────────────

export interface ResearchDetailDrawerProps {
	open: boolean;
	onClose: () => void;
	record: ResearchRecord | null;
	/** Whether the Xoe handoff affordance is shown (mirrors VITE_XOE_ENABLED). */
	xoeEnabled?: boolean;
	/** Starts a Xoe conversation seeded with this record. */
	onDiscuss?: (record: ResearchRecord) => void;
	/** True while the Xoe conversation is being created/seeded. */
	discussing?: boolean;
}

const ResearchDetailDrawer: React.FC<ResearchDetailDrawerProps> = ({
	open,
	onClose,
	record,
	xoeEnabled = false,
	onDiscuss,
	discussing = false,
}) => {
	const { showError } = useNotifications();
	const [downloading, setDownloading] = useState(false);
	const screens = Grid.useBreakpoint();
	/** Below lg the sidebar drops under the prose rather than squeezing both (§21). */
	const isWide = !!screens.lg;

	if (!record) {
		return <DetailDrawer open={open} onClose={onClose} title="Result" width="70vw" children={null} />;
	}

	const meta = record.meta ?? {};

	// ── Document download (reuses globalFileResourcesApi) ─────────────────────
	const fileResourceId = str(meta.global_file_resource_id);
	const handleDownload = async () => {
		if (!fileResourceId) return;
		try {
			setDownloading(true);
			await downloadGlobalFileResource(fileResourceId, record.title || 'document');
		} catch (e) {
			showError('Download failed', 'The document could not be downloaded.');
		} finally {
			setDownloading(false);
		}
	};

	const summary = record.snippet ? (
		<Field label="Summary">
			<Paragraph style={{ marginBottom: 0, fontSize: 'var(--mg-fs-md)' }}>{record.snippet}</Paragraph>
		</Field>
	) : null;

	// ── Type-specific body ─────────────────────────────────────────────────────
	// Each branch splits into `main` (prose + the action you came here to take) and `side`
	// (the facts you look up), so the 70vw is spent on the sidebar rather than on line length.
	const { main, side } = ((): { main: React.ReactNode; side: React.ReactNode } => {
		if (record.source_type === 'monograph') {
			return {
				main: (
					<>
						{summary}
						<Field label="Mechanism of action">{str(meta.mechanism_of_action) ?? '—'}</Field>
					</>
				),
				side: (
					<>
						<Field label="Generic name">{str(meta.generic_name) ?? '—'}</Field>
						<Field label="Drug class">{str(meta.drug_class) ?? '—'}</Field>
						<Field label="Evidence level">{str(meta.evidence_level) ?? '—'}</Field>
						<Field label="Indications">
							<TagList values={Array.isArray(meta.indications) ? (meta.indications as string[]) : undefined} />
						</Field>
						<Field label="Contraindications">
							<TagList
								values={Array.isArray(meta.contraindications) ? (meta.contraindications as string[]) : undefined}
							/>
						</Field>
					</>
				),
			};
		}

		if (record.source_type === 'clinic_document') {
			return {
				main: (
					<>
						{summary}
						{fileResourceId && (
							<Button
								type="primary"
								icon={<DownloadOutlined />}
								loading={downloading}
								onClick={handleDownload}
								data-testid="research-document-download"
								style={{ alignSelf: 'flex-start' }}
							>
								Download
							</Button>
						)}
					</>
				),
				side: (
					<>
						<Field label="Category">{str(meta.category) ?? '—'}</Field>
						<Field label="File type">{str(meta.file_type) ?? '—'}</Field>
						<Field label="File size">{humanFileSize(meta.file_size) ?? '—'}</Field>
					</>
				),
			};
		}

		if (record.source_type === 'resource_link') {
			const linkUrl = str(meta.link_url) ?? record.url;
			return {
				main: (
					<>
						{summary}
						{linkUrl && (
							<Link href={linkUrl} target="_blank" rel="noopener noreferrer">
								<ExportOutlined /> Open resource
							</Link>
						)}
					</>
				),
				side: <Field label="Resource type">{str(meta.resource_type) ?? '—'}</Field>,
			};
		}

		// Generic fallback for any unmodelled source type.
		return {
			main: (
				<>
					{summary}
					{record.url && (
						<Link href={record.url} target="_blank" rel="noopener noreferrer">
							<ExportOutlined /> Open
						</Link>
					)}
				</>
			),
			side: record.subtitle ? <Field label="Detail">{record.subtitle}</Field> : null,
		};
	})();

	return (
		<DetailDrawer
			open={open}
			onClose={onClose}
			width="70vw"
			title={record.title}
			subtitle={record.subtitle || undefined}
			footer={
				xoeEnabled && onDiscuss ? (
					<Button
						type="default"
						icon={<MessageOutlined />}
						loading={discussing}
						onClick={() => onDiscuss(record)}
						data-testid="research-discuss-xoe"
					>
						Discuss with Xoe
					</Button>
				) : undefined
			}
		>
			<div
				style={{
					display: 'flex',
					gap: 24,
					alignItems: 'flex-start',
					...(isWide ? {} : { flexDirection: 'column' }),
				}}
			>
				{/* Prose — capped measure, so 70vw never stretches a summary across the viewport. */}
				<div
					style={{
						flex: 1,
						minWidth: 0,
						maxWidth: isWide ? PROSE_MAX_W : undefined,
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{main}
				</div>

				{side && (
					<div
						style={{
							width: isWide ? META_COL_W : '100%',
							flexShrink: 0,
							...(isWide
								? { borderLeft: '1px solid var(--mg-border)', paddingLeft: 24 }
								: { borderTop: '1px solid var(--mg-border)', paddingTop: 12 }),
						}}
					>
						{side}
					</div>
				)}
			</div>
		</DetailDrawer>
	);
};

export default ResearchDetailDrawer;
